import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Spinner } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import Viewer from 'viewerjs';
import 'viewerjs/dist/viewer.css';
import { SourceElementType } from '../utils/constants';
import { useGetAgentKowledgeSourceMutation, useGetEvaluationsPresignedUrlMutation } from '@circuitry-ai/doc-data';
import { CtyShowToast, checkType } from '../toast/CtyToast';
import { formatKeyForDisplay } from '../utils/commonutils';
import { getFieldFormat, stripFieldSuffix } from './actionOutputV2Utils';

const VIDEO_EXTENSIONS = SourceElementType.VIDEO as readonly string[];
const AUDIO_EXTENSIONS = SourceElementType.AUDIO as readonly string[];

export const isVideoExtension = (ext: string): boolean =>
    !!ext && VIDEO_EXTENSIONS.includes(ext.toLowerCase());

export const isAudioExtension = (ext: string): boolean =>
    !!ext && AUDIO_EXTENSIONS.includes(ext.toLowerCase());

export type AttachmentSourcePayload = {
    source_id: string;
    filename: string;
    file_extension?: string;
    description?: string;
};

const getLoadFailedDisplay = (
    isVideo: boolean,
    isAudio: boolean,
    t: (key: string) => string
): { icon: string; message: string } => {
    if (isVideo) return { icon: 'mdi:video-off-outline', message: t('Video could not be loaded') };
    if (isAudio) return { icon: 'mdi:audio-off-outline', message: t('Audio could not be loaded') };
    return { icon: 'mdi:image-off-outline', message: t('Image could not be loaded') };
};

export const AttachmentMediaWithFallback: React.FC<{
    url: string;
    alt: string;
    isVideo: boolean;
    isAudio: boolean;
}> = ({ url, alt, isVideo, isAudio }) => {
    const { t } = useTranslation();
    const [loadFailed, setLoadFailed] = useState(false);
    const imageContainerRef = useRef<HTMLDivElement | null>(null);
    const viewerRef = useRef<Viewer | null>(null);

    useEffect(() => {
        if (isVideo || isAudio || !url || loadFailed) return;
        const el = imageContainerRef.current;
        if (!el) return;
        if (viewerRef.current) {
            viewerRef.current.destroy();
            viewerRef.current = null;
        }
        viewerRef.current = new Viewer(el, { toolbar: true, navbar: false });
        return () => {
            viewerRef.current?.destroy();
            viewerRef.current = null;
        };
    }, [url, isVideo, isAudio, loadFailed]);

    const loadFailedClass =
        'flex flex-col items-center justify-center rounded-lg border border-default-200 gap-2 text-default-500';

    if (loadFailed) {
        const { icon, message } = getLoadFailedDisplay(isVideo, isAudio, t);
        return (
            <div className={`${loadFailedClass} min-h-[200px]`} aria-label={alt}>
                <Icon icon={icon} className="w-12 h-12 text-default-400" aria-hidden />
                <p className="text-sm">{message}</p>
            </div>
        );
    }

    if (isVideo) {
        return (
            <video
                src={url}
                controls
                className="max-w-full rounded-lg border border-default-200 object-contain max-h-[320px] bg-black"
                onError={() => setLoadFailed(true)}
                aria-label={alt}
            >
                <track kind="captions" srcLang="en" label={t('Captions')} />
                {t('Your browser does not support video playback.')}
            </video>
        );
    }

    if (isAudio) {
        return (
            <audio
                src={url}
                controls
                className="max-w-full rounded-lg border border-default-200 w-full"
                onError={() => setLoadFailed(true)}
                aria-label={alt}
            >
                <track kind="captions" srcLang="en" label={t('Captions')} />
                {t('Your browser does not support audio playback.')}
            </audio>
        );
    }

    return (
        <div className="relative inline-block max-w-full">
            <div ref={imageContainerRef} className="max-w-full">
                <img
                    src={url}
                    alt={alt}
                    className="max-w-full rounded-lg border border-default-200 object-contain max-h-[320px]"
                    onError={() => setLoadFailed(true)}
                />
            </div>
            <Button
                size="sm"
                onPress={() => viewerRef.current?.show()}
                className="absolute bottom-3 left-3 z-10 rounded-md bg-content1 text-default-700 shadow-md hover:bg-default-100 border border-default-200 min-w-0"
                aria-label={t('View image')}
            >
                {t('View image')}
            </Button>
        </div>
    );
};

export const normalizeAttachmentItems = (value: unknown): AttachmentSourcePayload[] => {
    if (value == null) return [];
    const list = Array.isArray(value) ? value : [value];
    return list
        .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item))
        .map((item) => {
            const rawName = item.filename ?? item.file_name ?? 'attachment';
            const filename = typeof rawName === 'string' || typeof rawName === 'number' ? String(rawName) : 'attachment';
            const ext = filename.includes('.') ? filename.split('.').pop()?.toLowerCase() : '';
            const rawId = item.source_id;
            const source_id = typeof rawId === 'string' || typeof rawId === 'number' ? String(rawId) : '';
            const description = typeof item.description === 'string' ? item.description.trim() : '';
            return {
                source_id,
                filename,
                file_extension: ext,
                ...(description ? { description } : {}),
            };
        })
        .filter((item) => item.source_id);
};

export const inferMediaTypeFromFieldKey = (fieldKey: string): { isVideo: boolean; isAudio: boolean } => {
    const base = stripFieldSuffix(fieldKey).toLowerCase();
    return {
        isVideo: base.includes('video'),
        isAudio: base.includes('audio'),
    };
};

export const collectAttachmentSourcesFromValue = (
    value: unknown,
    refs: AttachmentSourcePayload[],
    seen: Set<string>
): void => {
    if (value == null) return;
    if (Array.isArray(value)) {
        value.forEach((item) => collectAttachmentSourcesFromValue(item, refs, seen));
        return;
    }
    if (typeof value !== 'object') return;

    const record = value as Record<string, unknown>;
    for (const [key, val] of Object.entries(record)) {
        if (getFieldFormat(key) === 'attachment') {
            for (const item of normalizeAttachmentItems(val)) {
                if (!seen.has(item.source_id)) {
                    seen.add(item.source_id);
                    refs.push(item);
                }
            }
        } else {
            collectAttachmentSourcesFromValue(val, refs, seen);
        }
    }
};

export const isAttachmentFieldKey = (key: string): boolean => getFieldFormat(key) === 'attachment';

export const getAttachmentS3Keys = (value: unknown): string[] => {
    let raw: unknown;
    if (Array.isArray(value)) raw = value;
    else if (value && typeof value === 'object') raw = (value as { s3_keys?: unknown }).s3_keys;
    return Array.isArray(raw) ? raw.filter((k): k is string => typeof k === 'string' && k.length > 0) : [];
};

export const collectAttachmentS3KeysDeep = (
    value: unknown,
    acc: string[] = [],
    seen: Set<string> = new Set<string>(),
): string[] => {
    if (value == null) return acc;
    if (Array.isArray(value)) {
        value.forEach((item) => collectAttachmentS3KeysDeep(item, acc, seen));
        return acc;
    }
    if (typeof value !== 'object') return acc;
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
        if (isAttachmentFieldKey(key)) {
            getAttachmentS3Keys(val).forEach((k) => {
                if (!seen.has(k)) {
                    seen.add(k);
                    acc.push(k);
                }
            });
        } else {
            collectAttachmentS3KeysDeep(val, acc, seen);
        }
    }
    return acc;
};

export function useAttachmentUrlMap(s3Keys: string[]) {
    const { t } = useTranslation();
    const [getPresignedUrlsByKey, { isLoading }] = useGetEvaluationsPresignedUrlMutation();
    const [urlByKey, setUrlByKey] = useState<Record<string, string>>({});
    const [fetchFailed, setFetchFailed] = useState(false);
    const fetchKeyRef = useRef<string | null>(null);

    const keys = useMemo(() => Array.from(new Set(s3Keys.filter(Boolean))), [s3Keys]);

    useEffect(() => {
        if (!keys.length) return;
        const cacheKey = [...keys].sort((a, b) => a.localeCompare(b)).join(',');
        if (fetchKeyRef.current === cacheKey) return;
        fetchKeyRef.current = cacheKey;
        setFetchFailed(false);

        getPresignedUrlsByKey({ s3_keys: keys })
            .unwrap()
            .then((res: unknown) => {
                const list = Array.isArray(res) ? res : (res as { data?: unknown })?.data;
                const arr: Array<{ s3_key?: string; presigned_url?: string }> = Array.isArray(list) ? list : [];
                const map: Record<string, string> = {};
                arr.forEach((d, i) => {
                    // Prefer the returned key; fall back to positional mapping when the response omits `s3_key`.
                    const key = d?.s3_key ?? keys[i];
                    if (key && d?.presigned_url) map[key] = d.presigned_url;
                });
                setUrlByKey(map);
            })
            .catch((err: { data?: { message?: string }; message?: string }) => {
                CtyShowToast(checkType.error, err?.data?.message ?? err?.message ?? t('Failed to load attachment'));
                setFetchFailed(true);
            });
    }, [keys, getPresignedUrlsByKey, t]);

    return { urlByKey, isLoading, fetchFailed };
}

type AttachmentUrlContextValue = {
    urlByKey: Record<string, string>;
    isLoading: boolean;
    fetchFailed: boolean;
};

const AttachmentUrlContext = createContext<AttachmentUrlContextValue>({
    urlByKey: {},
    isLoading: false,
    fetchFailed: false,
});

export const AttachmentUrlProvider: React.FC<{ data: unknown; children: React.ReactNode }> = ({ data, children }) => {
    const s3Keys = useMemo(() => collectAttachmentS3KeysDeep(data), [data]);
    const { urlByKey, isLoading, fetchFailed } = useAttachmentUrlMap(s3Keys);
    const value = useMemo(() => ({ urlByKey, isLoading, fetchFailed }), [urlByKey, isLoading, fetchFailed]);
    return <AttachmentUrlContext.Provider value={value}>{children}</AttachmentUrlContext.Provider>;
};

export const AttachmentByKeyBlock: React.FC<{ fieldKey: string; value: unknown }> = ({ fieldKey, value }) => {
    const { t } = useTranslation();
    const { urlByKey, isLoading, fetchFailed } = useContext(AttachmentUrlContext);
    const s3Keys = useMemo(() => getAttachmentS3Keys(value), [value]);
    const urls = useMemo(
        () => s3Keys.map((k) => urlByKey[k]).filter((u): u is string => !!u),
        [s3Keys, urlByKey],
    );
    const baseLabel = stripFieldSuffix(fieldKey);
    const label = baseLabel ? formatKeyForDisplay(baseLabel) : '';
    const mediaHint = inferMediaTypeFromFieldKey(fieldKey);

    if (s3Keys.length === 0) return null;

    return (
        <div className="mb-2">
            {label ? <div className="capitalize font-medium text-default-700 mb-2">{label}</div> : null}
            <div className="space-y-3">
                {isLoading && urls.length === 0 && !fetchFailed ? <Spinner size="sm" aria-label={t('Loading')} /> : null}
                {fetchFailed && urls.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-lg border border-default-200 gap-2 text-default-500 p-4">
                        <Icon icon="mdi:image-off-outline" className="w-8 h-8 text-default-400" aria-hidden />
                        <p className="text-sm">{t('Attachment could not be loaded')}</p>
                    </div>
                ) : null}
                {urls.map((url) => (
                    <AttachmentMediaWithFallback
                        key={url}
                        url={url}
                        alt={label || t('Attachment')}
                        isVideo={mediaHint.isVideo}
                        isAudio={mediaHint.isAudio}
                    />
                ))}
            </div>
        </div>
    );
};

/** Fetch presigned URLs for V2 @attachment fields (same API as legacy image_scores). */
export function useV2AttachmentPresignedUrls(sourceIds: AttachmentSourcePayload[]) {
    const { t } = useTranslation();
    const [getAgentPresignedUrls, { isLoading }] = useGetAgentKowledgeSourceMutation();
    const [urlsBySourceId, setUrlsBySourceId] = useState<Record<string, string>>({});
    const [fetchFailed, setFetchFailed] = useState(false);
    const fetchKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (!sourceIds.length) return;
        const key = sourceIds
            .map((s) => s.source_id)
            .sort((a, b) => a.localeCompare(b))
            .join(',');
        if (fetchKeyRef.current === key) return;
        fetchKeyRef.current = key;
        setFetchFailed(false);

        const presignedRequest = {
            source_ids: sourceIds.map((s) => ({
                source_id: s.source_id,
                file_name: s.filename,
                file_extension: s.file_extension ?? '',
            })),
        };
        // RTK types require a non-empty tuple; length is guarded above (same shape as legacy image_scores).
        getAgentPresignedUrls(presignedRequest as Parameters<typeof getAgentPresignedUrls>[0])
            .unwrap()
            .then((res: { data?: Array<{ source_id?: string; full_source_presigned_url?: string }> }) => {
                const next: Record<string, string> = {};
                (res?.data ?? []).forEach((d) => {
                    if (d?.source_id && d?.full_source_presigned_url) {
                        next[d.source_id] = d.full_source_presigned_url;
                    }
                });
                setUrlsBySourceId((prev) => ({ ...prev, ...next }));
            })
            .catch((err: { data?: { message?: string }; message?: string }) => {
                const message = err?.data?.message ?? err?.message ?? t('Failed to load attachment');
                CtyShowToast(checkType.error, message);
                setFetchFailed(true);
            });
    }, [sourceIds, getAgentPresignedUrls, t]);

    return { urlsBySourceId, isLoading, fetchFailed };
}
